import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { io, Socket } from 'socket.io-client';
import { JobDetail } from '../features/jobs/jobTypes';
import { getJobsWithMcp } from '../features/jobs/mcpApi';

interface LiveJobState {
  [jobId: string]: JobDetail;
}

interface JobMetrics {
  active: number;
  waiting: number;
  completed: number;
  failed: number;
  delayed: number;
  paused: number;
}

interface DocumentJobMetrics {
  totalDocuments: number;
  documentsProcessingToday: number;
  avgProcessingTimeMs: number;
  successRate: number;
  failureRate: number;
  mostCommonFailure?: string;
  activeDocumentJobs: number;
  completedDocumentJobs: number;
  failedDocumentJobs: number;
}

interface AgentActivity {
  agentName: string;
  status: 'active' | 'idle' | 'failed';
  currentJobId?: string;
  lastActivity: string;
}

interface LiveEvent {
  id: string;
  type: string;
  timestamp: string;
  jobId?: string;
  jobName?: string;
  agentName?: string;
  realmId?: string;
  status?: string;
  progress?: number;
  phase?: string;
  icon: string;
  color: string;
  message: string;
  data: any;
}

// WebSocket Log Entry (for raw WebSocket event logging)
interface WsLogEntry {
  id: string;
  timestamp: string;
  channel: string;
  level?: 'debug' | 'info' | 'warn' | 'error';
  realmId?: string;
  jobId?: string;
  jobName?: string;
  message?: string;
  payload: any;
  icon?: string;
  color?: string;
}

// WebSocket Log Filters
interface WsLogFilters {
  channels: string[];
  realms: string[];
  jobs: string[];
  levels: string[];
  timeRange?: {
    start: Date;
    end: Date;
  };
}

interface LiveJobStoreState {
  // WebSocket connection
  socket: Socket | null;
  connected: boolean;
  
  // Job data
  jobsById: LiveJobState;
  agentActivity: AgentActivity[];
  recentEvents: LiveEvent[];
  
  // WebSocket Logs (raw event logging)
  wsLogs: WsLogEntry[];
  wsLogFilters: WsLogFilters;
  wsLogsPaused: boolean;
  
  // Computed metrics (will be calculated)
  metrics: JobMetrics;
  documentMetrics: DocumentJobMetrics;
  
  // ✅ NEW: Telemetry metrics
  performanceMetrics: Record<string, any>;
  costMetrics: Record<string, any>;
  
  // Status
  lastUpdated: Date | null;
  error: string | null;
  
  // Actions
  initializeSocket: (token: string, realmId: string | null, adminMode?: boolean) => void;
  disconnectSocket: () => void;
  loadExistingJobs: (token: string, realmId: string | null, adminMode?: boolean) => Promise<void>;
  updateJob: (jobUpdate: Partial<JobDetail> & { id: string }) => void;
  addEvent: (event: Omit<LiveEvent, 'id' | 'timestamp'>) => void;
  updateAgentActivity: (agent: AgentActivity) => void;
  clearError: () => void;
  clearAllData: () => void;
  
  // WebSocket Log Actions
  addWsLog: (entry: Omit<WsLogEntry, 'id' | 'timestamp'>) => void;
  updateWsLogFilters: (filters: Partial<WsLogFilters>) => void;
  toggleWsLogsPause: () => void;
  clearWsLogs: () => void;
  getFilteredWsLogs: () => WsLogEntry[];
  
  // ✅ NEW: Telemetry-specific state methods
  updatePerformanceMetrics: (metrics: any) => void;
  updateCostMetrics: (costs: any) => void;
}

const DOCUMENT_JOBS = [
  'DocumentUploadIngestJob',
      'DoclingExtractionJob', 
  'MetadataExtractionJob',
  'DocumentVectorizationJob',
  'DocumentIngestJob'
];

const isDocumentJob = (jobName: string) => {
  return DOCUMENT_JOBS.some(docJob => jobName.includes(docJob));
};

// Helper function to calculate metrics
const calculateMetrics = (jobsById: LiveJobState): JobMetrics => {
  const jobs = Object.values(jobsById);
  return {
    active: jobs.filter(job => job.status === 'active').length,
    waiting: jobs.filter(job => job.status === 'waiting').length,
    completed: jobs.filter(job => job.status === 'completed').length,
    failed: jobs.filter(job => job.status === 'failed').length,
    delayed: jobs.filter(job => job.status === 'delayed').length,
    paused: jobs.filter(job => job.status === 'paused').length,
  };
};

// Helper function to calculate document metrics
const calculateDocumentMetrics = (jobsById: LiveJobState): DocumentJobMetrics => {
  const jobs = Object.values(jobsById);
  const documentJobs = jobs.filter(job => isDocumentJob(job.name));
  const completedDocJobs = documentJobs.filter(job => job.status === 'completed');
  const failedDocJobs = documentJobs.filter(job => job.status === 'failed');
  
  const totalProcessingTime = completedDocJobs.reduce((total, job) => {
    if (job.finishedOn && job.processedOn) {
      return total + (new Date(job.finishedOn).getTime() - new Date(job.processedOn).getTime());
    }
    return total;
  }, 0);
  
  const avgProcessingTimeMs = completedDocJobs.length > 0 
    ? totalProcessingTime / completedDocJobs.length 
    : 0;
  
  const totalProcessedJobs = completedDocJobs.length + failedDocJobs.length;
  const successRate = totalProcessedJobs > 0 ? completedDocJobs.length / totalProcessedJobs : 0;
  const failureRate = totalProcessedJobs > 0 ? failedDocJobs.length / totalProcessedJobs : 0;
  
  const failureReasons = failedDocJobs
    .map(job => job.failedReason)
    .filter((reason): reason is string => Boolean(reason));
  const mostCommonFailure = failureReasons.length > 0 
    ? failureReasons.reduce((a, b, _, arr) => 
        arr.filter(v => v === a).length >= arr.filter(v => v === b).length ? a : b
      )
    : undefined;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const documentsProcessingToday = documentJobs.filter(job => {
    if (!job.createdAt) return false;
    const jobDate = new Date(job.createdAt);
    return jobDate >= today;
  }).length;
  
  return {
    totalDocuments: documentJobs.length,
    documentsProcessingToday,
    avgProcessingTimeMs,
    successRate,
    failureRate,
    mostCommonFailure,
    activeDocumentJobs: documentJobs.filter(job => job.status === 'active').length,
    completedDocumentJobs: completedDocJobs.length,
    failedDocumentJobs: failedDocJobs.length,
  };
};

export const useLiveJobStore = create<LiveJobStoreState>()(
  devtools(
    (set, get) => ({
      // Initial state
      socket: null,
      connected: false,
      jobsById: {},
      agentActivity: [],
      recentEvents: [],
      
      // WebSocket Logs initial state
      wsLogs: [],
      wsLogFilters: {
        channels: [],
        realms: [],
        jobs: [],
        levels: [],
      },
      wsLogsPaused: false,
      
      metrics: {
        active: 0,
        waiting: 0,
        completed: 0,
        failed: 0,
        delayed: 0,
        paused: 0,
      },
      documentMetrics: {
        totalDocuments: 0,
        documentsProcessingToday: 0,
        avgProcessingTimeMs: 0,
        successRate: 0,
        failureRate: 0,
        activeDocumentJobs: 0,
        completedDocumentJobs: 0,
        failedDocumentJobs: 0,
      },
      lastUpdated: null,
      error: null,

      // ✅ NEW: Telemetry state
      performanceMetrics: {},
      costMetrics: {},

      // Actions
      loadExistingJobs: async (token: string, realmId: string | null, adminMode = false) => {
        try {
          console.log(`[LiveJobStore] Loading existing jobs... (realm: ${realmId || 'ALL'}, admin: ${adminMode})`);
          
          // In admin mode, we might want to fetch from multiple realms or use a different API
          // For now, if realmId is null, we'll use a placeholder and filter later
          const effectiveRealmId = realmId || 'admin-all-realms';
          
          // Fetch jobs from all statuses using MCP API
          const [activeJobs, waitingJobs, completedJobs, failedJobs] = await Promise.all([
            getJobsWithMcp({ realmId: effectiveRealmId, status: 'active' }, token),
            getJobsWithMcp({ realmId: effectiveRealmId, status: 'waiting' }, token), 
            getJobsWithMcp({ realmId: effectiveRealmId, status: 'completed' }, token),
            getJobsWithMcp({ realmId: effectiveRealmId, status: 'failed' }, token)
          ]);
          
          // Convert JobSummary to JobDetail format and combine all jobs
          const allJobs = [...activeJobs, ...waitingJobs, ...completedJobs, ...failedJobs];
          const jobsById: LiveJobState = {};
          
          allJobs.forEach(job => {
            jobsById[job.id] = {
              id: job.id,
              name: job.name,
              status: job.status,
              attempts: job.attempts || 0,
              data: job.data || {},
              failedReason: null,
              stacktrace: [],
              createdAt: new Date().toISOString() // Set current time for existing jobs
            };
          });
          
          // 🛡️ Apply memory management to initial load
          const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days
          const maxJobs = 1000; // Hard limit
          const now = new Date().getTime();
          
          // Remove old jobs
          let cleanedJobs = Object.fromEntries(
            Object.entries(jobsById).filter(([_, job]) => {
              if (!job.createdAt) return true;
              return (now - new Date(job.createdAt).getTime()) < maxAge;
            })
          );
          
          // Apply hard limit if needed
          const jobEntries = Object.entries(cleanedJobs);
          if (jobEntries.length > maxJobs) {
            const sortedJobs = jobEntries.sort((a, b) => {
              const dateA = new Date(a[1].createdAt || 0).getTime();
              const dateB = new Date(b[1].createdAt || 0).getTime();
              return dateB - dateA; // Newest first
            });
            cleanedJobs = Object.fromEntries(sortedJobs.slice(0, maxJobs));
          }
          
          // Update store with cleaned jobs
          set({
            jobsById: cleanedJobs,
            metrics: calculateMetrics(cleanedJobs),
            documentMetrics: calculateDocumentMetrics(cleanedJobs),
            lastUpdated: new Date()
          });
          
          console.log(`[LiveJobStore] Loaded ${allJobs.length} existing jobs`);
          
          // Add individual events for interesting jobs instead of generic message
          const interestingJobs = Object.values(cleanedJobs).filter(job => 
            job.status === 'active' || 
            job.status === 'failed' || 
            (job.status === 'completed' && job.finishedOn && 
             (new Date().getTime() - new Date(job.finishedOn).getTime()) < 60 * 60 * 1000) // Completed in last hour
          );

          // Add events for each interesting job
          interestingJobs.forEach(job => {
            const isDocumentJob = job.name && DOCUMENT_JOBS.some(docJob => job.name.includes(docJob));
            const documentName = job.data?._meta?.fileName || job.data?.fileName || 'Unknown';
            
            if (job.status === 'active') {
              get().addEvent({
                type: 'job:active-discovered',
                jobId: job.id,
                jobName: job.name,
                message: `🔄 Active: ${job.name}${isDocumentJob ? ` (${documentName})` : ''}${job.progress ? ` - ${job.progress}%` : ''}`,
                icon: isDocumentJob ? '📄' : '⚙️',
                color: 'text-blue-600',
                data: { ...job, isDocumentJob, documentName }
              });
            } else if (job.status === 'failed') {
              get().addEvent({
                type: 'job:failed-discovered',
                jobId: job.id,
                jobName: job.name,
                message: `❌ Failed: ${job.name}${isDocumentJob ? ` (${documentName})` : ''} - ${job.failedReason || 'Unknown error'}`,
                icon: '💥',
                color: 'text-red-600',
                data: { ...job, isDocumentJob, documentName }
              });
            } else if (job.status === 'completed') {
              get().addEvent({
                type: 'job:completed-discovered',
                jobId: job.id,
                jobName: job.name,
                message: `✅ Recently completed: ${job.name}${isDocumentJob ? ` (${documentName})` : ''}`,
                icon: isDocumentJob ? '📄' : '✅',
                color: 'text-green-600',
                data: { ...job, isDocumentJob, documentName }
              });
            }
          });

          // Add summary event only if no interesting jobs were found
          if (interestingJobs.length === 0) {
            get().addEvent({
              type: 'system:jobs-loaded',
              message: `📋 Loaded ${Object.keys(cleanedJobs).length} jobs (all idle/old)`,
              icon: '📋',
              color: 'text-gray-600',
              data: { totalCount: Object.keys(cleanedJobs).length, breakdown: calculateMetrics(cleanedJobs) }
            });
          } else {
            get().addEvent({
              type: 'system:jobs-loaded',
              message: `📋 Loaded ${Object.keys(cleanedJobs).length} total jobs (${interestingJobs.length} active/recent)`,
              icon: '📋',
              color: 'text-blue-600',
              data: { totalCount: Object.keys(cleanedJobs).length, interestingCount: interestingJobs.length }
            });
          }
          
        } catch (error) {
          console.error('[LiveJobStore] Error loading existing jobs:', error);
          set({ error: `Failed to load existing jobs: ${error}` });
        }
      },

      initializeSocket: (token: string, realmId: string | null, adminMode = false) => {
        const currentSocket = get().socket;
        
        // Don't create multiple connections
        if (currentSocket?.connected) {
          console.log('[LiveJobStore] Socket already connected');
          return;
        }

        // Disconnect existing socket if any
        if (currentSocket) {
          currentSocket.disconnect();
        }

        console.log(`🔌 [LiveJobStore] ATTEMPTING WebSocket connection with auth (realm: ${realmId || 'ALL'}, admin: ${adminMode})`);
        console.log(`🔌 [LiveJobStore] Token provided: ${token ? 'YES' : 'NO'}, length: ${token?.length || 0}`);
        console.log(`🔌 [LiveJobStore] Connecting to: ${window.location.origin}/socket.io`);

        // ✅ ENHANCED: Add authentication and telemetry
        // EASIER FIX: Use existing API proxy path for WebSocket (no nginx changes needed)
        const socket = io('/', {
          path: '/socket.io',  // Use /api/ proxy path instead of /socket.io
          transports: ['websocket', 'polling'], // Allow fallback
          auth: { 
            token,      // ✅ NEW: JWT authentication
            realmId,    // ✅ NEW: Realm context
            adminMode   // ✅ NEW: Admin mode flag
          },
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          timeout: 20000
        });

        // ✅ ENHANCED: Better connection handling
        socket.on('connect', () => {
          console.log('✅ [LiveJobStore] Authenticated WebSocket connected');
          set({ connected: true, error: null });
          
          // ✅ NEW: Subscribe to telemetry events
          socket.emit('subscribe:telemetry', [
            'agent:start', 'agent:success', 'agent:failure',
            'performance', 'llm:execution', 'cost:analysis'
          ]);
          
          // Add to WebSocket logs
          get().addWsLog({
            channel: 'job:connected',
            level: 'info',
            message: 'WebSocket connected successfully with authentication',
            payload: { socketId: socket.id, timestamp: new Date().toISOString() },
            icon: '🔌',
            color: 'text-green-600'
          });
          
          // Add connection event
          get().addEvent({
            type: 'system:connected',
            message: `Connected to ${realmId} event stream`,
            icon: '🟢',
            color: 'text-green-600',
            data: { socketId: socket.id, realmId }
          });
        });

        socket.on('connect_error', (err) => {
          console.error('❌ [LiveJobStore] Authentication failed:', err);
          set({ 
            connected: false, 
            error: `Authentication failed: ${err.message}` 
          });
          
          // Add to WebSocket logs
          get().addWsLog({
            channel: 'connection:error',
            level: 'error',
            message: `WebSocket authentication failed: ${err.message}`,
            payload: { error: err.message },
            icon: '❌',
            color: 'text-red-600'
          });
        });

        socket.on('disconnect', (reason) => {
          console.warn('[LiveJobStore] Disconnected:', reason);
          set({ connected: false });
          
          // Add to WebSocket logs
          get().addWsLog({
            channel: 'connection:disconnect',
            level: 'warn',
            message: `WebSocket disconnected: ${reason}`,
            payload: { reason },
            icon: '🔌',
            color: 'text-yellow-600'
          });
          
          get().addEvent({
            type: 'system:disconnected',
            message: `Disconnected: ${reason}`,
            icon: '🔴',
            color: 'text-red-600',
            data: { reason }
          });
        });

        // ✅ NEW: Enhanced telemetry event handlers with job status integration
        socket.on('telemetry:agent:start', (data) => {
          console.log('[liveJobStore] Received telemetry:agent:start', data);
          
          // ✅ CRITICAL: Create or update job to 'active' status for animations
          if (data.traceId) {
            const syntheticJob: JobDetail = {
              id: data.traceId,
              name: 'DocumentUploadIngestJob',
              status: 'active', // ✅ This enables animations!
              attempts: 0,
              createdAt: new Date().toISOString(),
              data: {
                documentId: data.documentId,
                agentName: data.agentName,
                operation: data.operation,
                _meta: {
                  realmId: data.realmId,
                  traceId: data.traceId
                }
              },
              failedReason: null,
              stacktrace: [],
              progressPhase: data.operation || data.agentName
            };
            
            get().updateJob(syntheticJob);
            console.log('[liveJobStore] Created/updated synthetic job for telemetry', syntheticJob.id);
          }
          
          get().addEvent({
            type: 'telemetry:agent:start',
            jobId: data.traceId, // ✅ CRITICAL: Use traceId as jobId
            message: `🤖 ${data.agentName} started: ${data.operation}`,
            icon: '🤖',
            color: 'text-blue-600',
            data
          });
        });

        socket.on('telemetry:agent:success', (data) => {
          console.log('[liveJobStore] Received telemetry:agent:success', data);
          
          // ✅ CRITICAL: Keep job active during processing pipeline
          if (data.traceId) {
            // Only mark as completed if this is the final agent
            const isFinalAgent = data.agentName === 'DocumentClassificationAgent';
            
            get().updateJob({
              id: data.traceId,
              status: isFinalAgent ? 'completed' : 'active', // Stay active until final agent
              progressPhase: isFinalAgent ? 'completed' : data.operation || data.agentName,
              finishedOn: isFinalAgent ? new Date().toISOString() : undefined
            });
            
            console.log('[liveJobStore] Updated job for agent success', { 
              traceId: data.traceId, 
              agent: data.agentName, 
              isFinal: isFinalAgent 
            });
          }
          
          get().addEvent({
            type: 'telemetry:agent:success',
            jobId: data.traceId, // ✅ CRITICAL: Use traceId as jobId
            message: `✅ ${data.agentName} completed: ${data.operation} (${data.executionTime}ms)`,
            icon: '✅',
            color: 'text-green-600',
            data
          });
        });

        socket.on('telemetry:agent:failure', (data) => {
          console.log('[liveJobStore] Received telemetry:agent:failure', data);
          
          // ✅ CRITICAL: Mark job as failed for error display
          if (data.traceId) {
            get().updateJob({
              id: data.traceId,
              status: 'failed',
              failedReason: data.errorMessage || data.error?.message || 'Agent execution failed',
              finishedOn: new Date().toISOString()
            });
            
            console.log('[liveJobStore] Marked job as failed', data.traceId);
          }
          
          get().addEvent({
            type: 'telemetry:agent:failure',
            jobId: data.traceId, // ✅ CRITICAL: Use traceId as jobId
            message: `❌ ${data.agentName} failed: ${data.errorMessage}`,
            icon: '❌',
            color: 'text-red-600',
            data
          });
        });

        socket.on('telemetry:performance', (data) => {
          get().updatePerformanceMetrics(data);
        });

        socket.on('telemetry:llm:execution', (data) => {
          get().addEvent({
            type: 'telemetry:llm:execution',
            message: `🧠 LLM ${data.provider}/${data.model}: ${data.totalTokens} tokens ($${data.cost.toFixed(4)})`,
            icon: '🧠',
            color: 'text-purple-600',
            data
          });
        });

        socket.on('telemetry:cost:analysis', (data) => {
          get().updateCostMetrics(data);
        });

        // ✅ KEEP: All existing BullMQ event handlers work as-is
        // Job lifecycle events
        socket.on('job:created', (payload) => {
          // Add to WebSocket logs
          get().addWsLog({
            channel: 'job:created',
            level: 'info',
            realmId: payload.realmId,
            jobId: payload.jobId,
            jobName: payload.name,
            message: `Job created: ${payload.name}`,
            payload,
            icon: '🆕',
            color: 'text-blue-600'
          });

          // In admin mode, filter by realm only if specific realm selected
          if (!adminMode && realmId && payload.realmId !== realmId) return;
          if (adminMode && realmId && payload.realmId !== realmId) return;
          
          const job: JobDetail = {
            id: payload.jobId,
            name: payload.name,
            status: 'waiting',
            attempts: 0,
            createdAt: payload.createdAt,
            data: payload.data,
            failedReason: null,
            stacktrace: []
          };
          
          get().updateJob(job);
          
          // Enhanced event message with context
          const isDocumentJob = payload.name && DOCUMENT_JOBS.some(docJob => payload.name.includes(docJob));
          const documentName = payload.data?._meta?.fileName || payload.data?.fileName;
          const jobDisplayName = payload.name.replace(/Job$/, ''); // Remove "Job" suffix for cleaner display
          
          get().addEvent({
            type: 'job:created',
            jobId: payload.jobId,
            jobName: payload.name,
            realmId: payload.realmId,
            message: `🆕 Queued: ${jobDisplayName}${documentName ? ` for "${documentName}"` : ''}`,
            icon: isDocumentJob ? '📄' : '🆕',
            color: 'text-blue-600',
            data: { ...payload, isDocumentJob, documentName }
          });
        });

        socket.on('job:started', (payload) => {
          if (!adminMode && realmId && payload.realmId !== realmId) return;
          if (adminMode && realmId && payload.realmId !== realmId) return;
          
          get().updateJob({
            id: payload.jobId,
            status: 'active',
            processedOn: payload.startedAt,
          });
          
          // Enhanced event message
          const isDocumentJob = payload.name && DOCUMENT_JOBS.some(docJob => payload.name.includes(docJob));
          const documentName = payload.data?._meta?.fileName || payload.data?.fileName;
          const jobDisplayName = payload.name.replace(/Job$/, '');
          const agentName = payload.agentName ? ` by ${payload.agentName.replace(/Agent$/, '')}` : '';
          
          get().addEvent({
            type: 'job:started',
            jobId: payload.jobId,
            jobName: payload.name,
            agentName: payload.agentName,
            realmId: payload.realmId,
            status: payload.status,
            message: `🚀 Processing: ${jobDisplayName}${documentName ? ` "${documentName}"` : ''}${agentName}`,
            icon: isDocumentJob ? '📄' : '🚀',
            color: 'text-blue-600',
            data: { ...payload, isDocumentJob, documentName }
          });
        });

        socket.on('job:progress', (payload) => {
          if (!adminMode && realmId && payload.realmId !== realmId) return;
          if (adminMode && realmId && payload.realmId !== realmId) return;
          
          get().updateJob({
            id: payload.jobId,
            progress: payload.progress,
            progressPhase: payload.phase,
            progressDetails: payload.details,
          });
          
          // Enhanced progress message
          const isDocumentJob = payload.name && DOCUMENT_JOBS.some(docJob => payload.name.includes(docJob));
          const documentName = payload.data?._meta?.fileName || payload.data?.fileName;
          const jobDisplayName = payload.name.replace(/Job$/, '');
          const phaseText = payload.phase ? `: ${payload.phase}` : '';
          
          get().addEvent({
            type: 'job:progress',
            jobId: payload.jobId,
            jobName: payload.name,
            realmId: payload.realmId,
            progress: payload.progress,
            phase: payload.phase,
            message: `⏳ ${jobDisplayName}${documentName ? ` "${documentName}"` : ''}${phaseText} (${payload.progress}%)`,
            icon: isDocumentJob ? '📄' : '⏳',
            color: 'text-yellow-600',
            data: { ...payload, isDocumentJob, documentName }
          });
        });

        socket.on('job:completed', (payload) => {
          if (!adminMode && realmId && payload.realmId !== realmId) return;
          if (adminMode && realmId && payload.realmId !== realmId) return;
          
          get().updateJob({
            id: payload.jobId,
            status: 'completed',
            finishedOn: payload.completedAt,
            attempts: payload.attempts,
            result: payload.result,
          });
          
          // Enhanced completion message
          const isDocumentJob = payload.name && DOCUMENT_JOBS.some(docJob => payload.name.includes(docJob));
          const documentName = payload.data?._meta?.fileName || payload.data?.fileName;
          const jobDisplayName = payload.name.replace(/Job$/, '');
          const duration = payload.startedAt && payload.completedAt ? 
            ` (${Math.round((new Date(payload.completedAt).getTime() - new Date(payload.startedAt).getTime()) / 1000)}s)` : '';
          
          get().addEvent({
            type: 'job:completed',
            jobId: payload.jobId,
            jobName: payload.name,
            realmId: payload.realmId,
            status: payload.status,
            message: `✅ Completed: ${jobDisplayName}${documentName ? ` "${documentName}"` : ''}${duration}`,
            icon: isDocumentJob ? '📄' : '✅',
            color: 'text-green-600',
            data: { ...payload, isDocumentJob, documentName }
          });
        });

        socket.on('job:failed', (payload) => {
          if (!adminMode && realmId && payload.realmId !== realmId) return;
          if (adminMode && realmId && payload.realmId !== realmId) return;
          
          get().updateJob({
            id: payload.jobId,
            status: 'failed',
            finishedOn: payload.failedAt,
            attempts: payload.attempts,
            failedReason: payload.error,
            stacktrace: payload.stackTrace ? [payload.stackTrace] : [],
          });
          
          // Enhanced failure message
          const isDocumentJob = payload.name && DOCUMENT_JOBS.some(docJob => payload.name.includes(docJob));
          const documentName = payload.data?._meta?.fileName || payload.data?.fileName;
          const jobDisplayName = payload.name.replace(/Job$/, '');
          const attemptText = payload.attempts > 1 ? ` (attempt ${payload.attempts})` : '';
          const errorText = payload.error ? `: ${payload.error.substring(0, 100)}${payload.error.length > 100 ? '...' : ''}` : '';
          
          get().addEvent({
            type: 'job:failed',
            jobId: payload.jobId,
            jobName: payload.name,
            realmId: payload.realmId,
            status: payload.status,
            message: `❌ Failed: ${jobDisplayName}${documentName ? ` "${documentName}"` : ''}${attemptText}${errorText}`,
            icon: '💥',
            color: 'text-red-600',
            data: { ...payload, isDocumentJob, documentName }
          });
        });

        // Agent events
        socket.on('agent:started', (payload) => {
          if (!adminMode && realmId && payload.realmId !== realmId) return;
          if (adminMode && realmId && payload.realmId !== realmId) return;
          
          get().updateAgentActivity({
            agentName: payload.agentName,
            status: 'active',
            currentJobId: payload.jobId,
            lastActivity: payload.timestamp,
          });
          
          // Enhanced agent message
          const agentDisplayName = payload.agentName.replace(/Agent$/, '');
          const jobDisplayName = payload.jobName ? payload.jobName.replace(/Job$/, '') : 'Unknown Job';
          
          get().addEvent({
            type: 'agent:started',
            jobId: payload.jobId,
            jobName: payload.jobName,
            agentName: payload.agentName,
            realmId: payload.realmId,
            message: `🤖 Agent ${agentDisplayName} started processing ${jobDisplayName}`,
            icon: '🤖',
            color: 'text-purple-600',
            data: { ...payload, agentDisplayName, jobDisplayName }
          });
        });

        socket.on('agent:completed', (payload) => {
          if (!adminMode && realmId && payload.realmId !== realmId) return;
          if (adminMode && realmId && payload.realmId !== realmId) return;
          
          const state = get();
          const updatedAgents = state.agentActivity.map(agent => 
            agent.agentName === payload.agentName 
              ? { ...agent, status: 'idle' as const, currentJobId: undefined, lastActivity: payload.timestamp }
              : agent
          );
          set({ agentActivity: updatedAgents });
          
          get().addEvent({
            type: 'agent:completed',
            jobId: payload.jobId,
            jobName: payload.jobName,
            agentName: payload.agentName,
            realmId: payload.realmId,
            message: `Agent completed: ${payload.agentName}`,
            icon: '🎯',
            color: 'text-green-600',
            data: payload
          });
        });

        socket.on('agent:failed', (payload) => {
          if (!adminMode && realmId && payload.realmId !== realmId) return;
          if (adminMode && realmId && payload.realmId !== realmId) return;
          
          const state = get();
          const updatedAgents = state.agentActivity.map(agent => 
            agent.agentName === payload.agentName 
              ? { ...agent, status: 'failed' as const, currentJobId: payload.jobId, lastActivity: payload.timestamp }
              : agent
          );
          set({ agentActivity: updatedAgents });
          
          get().addEvent({
            type: 'agent:failed',
            jobId: payload.jobId,
            jobName: payload.jobName,
            agentName: payload.agentName,
            realmId: payload.realmId,
            message: `Agent failed: ${payload.agentName} - ${payload.error}`,
            icon: '💥',
            color: 'text-red-600',
            data: payload
          });
        });

        // Schema validation events
        socket.on('schema:validation:failed', (payload) => {
          if (!adminMode && realmId && payload.realmId !== realmId) return;
          if (adminMode && realmId && payload.realmId !== realmId) return;
          
          get().addEvent({
            type: 'schema:validation:failed',
            jobId: payload.jobId,
            jobName: payload.jobName,
            realmId: payload.realmId,
            message: `Schema validation failed: ${payload.schema}`,
            icon: '⚠️',
            color: 'text-orange-600',
            data: payload
          });
        });

        // Generic WebSocket event logger (captures ALL events for logs)
        socket.onAny((eventName, ...args) => {
          const payload = args.length === 1 ? args[0] : args;
          
          // Skip logging certain noisy events
          const skipEvents = ['ping', 'pong'];
          if (skipEvents.includes(eventName)) return;
          
          // Skip events that have specific handlers to avoid duplicates
          const eventsWithSpecificHandlers = [
            'job:created', 'job:started', 'job:progress', 'job:completed', 'job:failed',
            'agent:started', 'agent:completed', 'agent:failed', 
            'schema:validation:failed'
          ];
          if (eventsWithSpecificHandlers.includes(eventName)) return;
          
          // Extract basic info from payload
          const realmId = payload?.realmId;
          const jobId = payload?.jobId;
          const jobName = payload?.name || payload?.jobName;
          const level = eventName.includes('error') || eventName.includes('failed') ? 'error' :
                       eventName.includes('warn') || eventName.includes('disconnect') ? 'warn' : 'info';
          
          // Determine icon and color based on event type
          let icon = '📡';
          let color = 'text-gray-600';
          
          if (eventName.includes('job:')) {
            if (eventName.includes('created')) { icon = '🆕'; color = 'text-blue-600'; }
            else if (eventName.includes('started')) { icon = '🚀'; color = 'text-blue-600'; }
            else if (eventName.includes('completed')) { icon = '✅'; color = 'text-green-600'; }
            else if (eventName.includes('failed')) { icon = '❌'; color = 'text-red-600'; }
            else if (eventName.includes('progress')) { icon = '⏳'; color = 'text-yellow-600'; }
          } else if (eventName.includes('agent:')) {
            icon = '🤖'; 
            color = eventName.includes('failed') ? 'text-red-600' : 'text-purple-600';
          } else if (eventName.includes('connection') || eventName.includes('connect')) {
            icon = '🔌';
            color = eventName.includes('error') ? 'text-red-600' : 'text-green-600';
          }
          
          get().addWsLog({
            channel: eventName,
            level,
            realmId,
            jobId,
            jobName,
            message: `${eventName}: ${jobName || jobId || 'system event'}`,
            payload,
            icon,
            color
          });
        });

        set({ socket, connected: false });
      },

      disconnectSocket: () => {
        const socket = get().socket;
        if (socket) {
          socket.disconnect();
          set({ socket: null, connected: false });
        }
      },

      updateJob: (jobUpdate) => {
        set((state) => {
          const updatedJobsById = {
            ...state.jobsById,
            [jobUpdate.id]: {
              ...state.jobsById[jobUpdate.id],
              ...jobUpdate,
            } as JobDetail
          };
          
          // 🛡️ Memory management: Hybrid approach (age + count limits)
          const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
          const maxJobs = 1000; // Hard limit for safety
          const now = new Date().getTime();
          
          // Step 1: Remove jobs older than 7 days
          let cleanedJobs = Object.fromEntries(
            Object.entries(updatedJobsById).filter(([_, job]) => {
              if (!job.createdAt) return true; // Keep jobs without creation date
              return (now - new Date(job.createdAt).getTime()) < maxAge;
            })
          );
          
          // Step 2: If still over hard limit, keep only the newest jobs
          const jobEntries = Object.entries(cleanedJobs);
          if (jobEntries.length > maxJobs) {
            const sortedJobs = jobEntries.sort((a, b) => {
              const dateA = new Date(a[1].createdAt || 0).getTime();
              const dateB = new Date(b[1].createdAt || 0).getTime();
              return dateB - dateA; // Newest first
            });
            cleanedJobs = Object.fromEntries(sortedJobs.slice(0, maxJobs));
          }
          
          return {
            jobsById: cleanedJobs,
            metrics: calculateMetrics(cleanedJobs),
            documentMetrics: calculateDocumentMetrics(cleanedJobs),
            lastUpdated: new Date(),
          };
        });
      },

      addEvent: (eventData) => {
        const event: LiveEvent = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          timestamp: new Date().toISOString(),
          ...eventData
        };
        
        set((state) => ({
          recentEvents: [event, ...state.recentEvents.slice(0, 99)] // Keep last 100 events
        }));
      },

      updateAgentActivity: (agent) => {
        set((state) => {
          const updatedAgents = state.agentActivity.filter(a => a.agentName !== agent.agentName);
          return {
            agentActivity: [...updatedAgents, agent]
          };
        });
      },

      clearError: () => set({ error: null }),

      clearAllData: () => set({
        jobsById: {},
        agentActivity: [],
        recentEvents: [],
        wsLogs: [],
        metrics: {
          active: 0,
          waiting: 0,
          completed: 0,
          failed: 0,
          delayed: 0,
          paused: 0,
        },
        documentMetrics: {
          totalDocuments: 0,
          documentsProcessingToday: 0,
          avgProcessingTimeMs: 0,
          successRate: 0,
          failureRate: 0,
          activeDocumentJobs: 0,
          completedDocumentJobs: 0,
          failedDocumentJobs: 0,
        },
        lastUpdated: null,
      }),

      // WebSocket Log Actions
      addWsLog: (entryData) => {
        const { wsLogsPaused } = get();
        if (wsLogsPaused) return;

        const entry: WsLogEntry = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          timestamp: new Date().toISOString(),
          ...entryData
        };

        set((state) => {
          // Memory management: Keep last 500 WebSocket logs
          const maxLogs = 500;
          const newLogs = [entry, ...state.wsLogs.slice(0, maxLogs - 1)];
          
          return { wsLogs: newLogs };
        });
      },

      updateWsLogFilters: (newFilters) => {
        set((state) => ({
          wsLogFilters: { ...state.wsLogFilters, ...newFilters }
        }));
      },

      toggleWsLogsPause: () => {
        set((state) => ({ wsLogsPaused: !state.wsLogsPaused }));
      },

      clearWsLogs: () => {
        set({ wsLogs: [] });
      },

      getFilteredWsLogs: () => {
        const { wsLogs, wsLogFilters } = get();
        
        return wsLogs.filter((log) => {
          // Channel filter
          if (wsLogFilters.channels.length > 0 && !wsLogFilters.channels.includes(log.channel)) {
            return false;
          }
          
          // Realm filter
          if (wsLogFilters.realms.length > 0 && log.realmId && !wsLogFilters.realms.includes(log.realmId)) {
            return false;
          }
          
          // Job filter
          if (wsLogFilters.jobs.length > 0 && log.jobName && !wsLogFilters.jobs.includes(log.jobName)) {
            return false;
          }
          
          // Level filter
          if (wsLogFilters.levels.length > 0 && log.level && !wsLogFilters.levels.includes(log.level)) {
            return false;
          }
          
          // Time range filter
          if (wsLogFilters.timeRange) {
            const logTime = new Date(log.timestamp);
            if (logTime < wsLogFilters.timeRange.start || logTime > wsLogFilters.timeRange.end) {
              return false;
            }
          }
          
          return true;
        });
      },

      // ✅ NEW: Telemetry-specific state methods
      updatePerformanceMetrics: (metrics) => {
        set((state) => ({ performanceMetrics: { ...state.performanceMetrics, ...metrics } }));
      },

      updateCostMetrics: (costs) => {
        set((state) => ({ costMetrics: { ...state.costMetrics, ...costs } }));
      },
    }),
    { name: 'live-job-store-devtools' }
  )
); 